import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Modal from "../../../../../components/Modal";
import EmptyState from "../../../../components/EmptyState";
import TatCell from "./TatCell";
import { formatDuration, formatShortDate } from "../../../../utils/format";

// TatTimelineModal — one order's journey through the stages the business measures.
//
// WHAT THIS SHOWS, AND WHAT IT DELIBERATELY DOES NOT.
//
// The first build of this modal rendered the raw status-transition log: every row the
// backend writes, in order. It was complete and it was unreadable. An operator saw
// `created -> Pending`, then `created -> Order Confirmed`, then
// `Pending -> Converted to Order` — three rows, two entities, one millisecond apart,
// none of which is a stage anyone measures. Internal status churn is not the journey;
// it is the machinery under it, and putting it on screen meant the reader had to know
// the status enums to find the four events that actually mattered.
//
// So this renders the STAGES, and the stages come from `meta.intervals` — the server's
// own list, the same one the table builds its columns from. That matters more than it
// looks: the set of stages is a product decision that changes (Confirmed -> Procurement
// was added after this page shipped), and the one thing this dashboard has been bitten
// by repeatedly is a client-side copy of a server list drifting from it (see
// src/v2/ops/constants/tatFilters.js's header). There is no hardcoded stage list here.
// The backend adds an interval, it appears in this modal and as a table column, same
// deploy, no frontend change.
//
// ONE ROW PER INTERVAL, NOT ONE PER POINT. A timeline of instants would be prettier —
// dots down a spine, each stage flowing into the next — and it would be wrong, because
// the stages branch: `Confirmed -> Procurement` and `Confirmed -> Packed` both start at
// the same boundary. A chain cannot express that; a list of measured spans can.
//
// Durations render through TatCell, unchanged. That is the whole point of reusing it:
// the seven-state contract (measured / in progress / stalled / missing / not tracked /
// invalid / admin-swept) is already the honest answer to "why is this cell not a
// number", and a second component inventing its own vocabulary for the same question
// would be two sources of truth about the same data.

/** The interval that spans the whole order rather than one step inside it. */
const SUMMARY_INTERVAL_KEY = "endToEnd";

/**
 * Below this, naming a "slowest stage" is noise rather than a finding.
 *
 * On an order that moved end to end in four minutes the slowest stage is still fast,
 * and decorating it would teach operators to discount the marker on the orders where it
 * is the whole story.
 */
export const SLOWEST_STAGE_FLOOR_MINUTES = 1;

/** A stage timestamp, or null. `stages` is keyed by the `from`/`to` names in meta. */
const stageTime = (stages, key) => {
    const raw = key ? stages?.[key] : null;
    if (!raw) return null;
    const time = new Date(raw).getTime();
    return Number.isNaN(time) ? null : time;
};

const formatClock = (time) =>
    new Date(time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

/**
 * When a span began and ended, written as briefly as it can be without losing the day.
 *
 * `withDate` is false when the whole order ran inside one day — the date is then stated
 * once above the list instead of seven times down it, which is the difference between a
 * column of times you can compare and a column of near-identical strings you cannot.
 * A span that crosses midnight always spells both ends out regardless, because that is
 * exactly when the bare clock would mislead.
 */
export function formatSpan(fromTime, toTime, withDate = true) {
    if (!fromTime && !toTime) return null;

    const stamp = (time) => (withDate ? `${formatShortDate(time)}, ${formatClock(time)}` : formatClock(time));

    if (fromTime && !toTime) return `${stamp(fromTime)} → still running`;
    if (!fromTime && toTime) return `not recorded → ${stamp(toTime)}`;

    const sameDay = formatShortDate(fromTime) === formatShortDate(toTime);
    if (!sameDay) {
        return `${formatShortDate(fromTime)}, ${formatClock(fromTime)} → ${formatShortDate(toTime)}, ${formatClock(toTime)}`;
    }
    return `${stamp(fromTime)} → ${formatClock(toTime)}`;
}

/**
 * The one date the whole order happened on, or null if it spans more than one.
 *
 * Drives `formatSpan`'s `withDate`: state it once in the header, or let every row carry
 * its own when the order really did run across days.
 */
export function singleDateFor(steps) {
    const dates = [];
    steps.forEach((step) => {
        [step.startedAt, step.endedAt].forEach((time) => {
            if (time) {
                const date = formatShortDate(time);
                if (!dates.includes(date)) dates.push(date);
            }
        });
    });
    return dates.length === 1 ? dates[0] : null;
}

/**
 * `timeline` -> the one event that set each stage timestamp, keyed by stage.
 *
 * This is the whole of what the transition log is used for, and the restraint is the
 * point. A real order carries a dozen-odd transitions — ORDER_ACTIVE to ORDER_PENDING
 * and back, four duplicate CONVERTED_TO_ORDER writes under four different request ids —
 * and rendering them was what made the first version of this dialog unreadable. Exactly
 * the events bearing a `stage` key are the ones that moved a measured boundary; nothing
 * else is read.
 *
 * Earliest write per stage wins. Those duplicate writes all carry the same `toStatus`
 * microseconds apart, and the one that actually set the timestamp is the first.
 *
 * @param {Array<object>|null|undefined} timeline
 * @returns {Object<string, object>} stage key -> the event that set it
 */
export function buildStageActors(timeline) {
    const byStage = {};
    (Array.isArray(timeline) ? timeline : []).forEach((event) => {
        if (!event?.stage) return;
        const at = new Date(event.at).getTime();
        if (Number.isNaN(at)) return;
        const existing = byStage[event.stage];
        if (!existing || at < new Date(existing.at).getTime()) byStage[event.stage] = event;
    });
    return byStage;
}

/**
 * Who moved a stage, in words an operator would use.
 *
 * A person's name when there is one. When there is not, the actor is a system and
 * saying so beats printing nothing: a quotation shared by the webhook was not shared by
 * nobody. `ADMIN_API` is not a thing anyone says out loud, so the enum is softened
 * rather than shown raw.
 */
export function stageAttribution(event) {
    if (!event) return null;
    if (event.actorName) return `by ${event.actorName}`;
    if (event.source) return `via ${event.source.toLowerCase().replace(/_/g, " ")}`;
    return null;
}

/**
 * Where a stage sits relative to the order: behind it, under it, or ahead of it.
 *
 * This exists because the old build was telling a plain untruth on the commonest row in
 * the dashboard. A four-minute-old order has five stages whose cells the server reports
 * as NO_DATA, and NO_DATA means "we measure this and this order lacks it" — so the modal
 * said data was missing for `Shipped → Delivered` on an order that has not been packed.
 * The order is not missing anything; it simply has not got there.
 *
 * The distinction is derived from the server's own `stages` timestamps, not invented:
 * a stage with NEITHER a start nor an end timestamp has not begun. Note both ends are
 * checked, not just the start — `Initial Lead → Quotation Creation` routinely has no
 * lead timestamp but a real quotation one, and that stage HAS been passed; its missing
 * figure is a genuine data gap and must keep saying so.
 *
 * NOT_TRACKED is never reclassified: the server saying "this is not measured here" is a
 * stronger statement than anything derivable from timestamps.
 *
 * @returns {"DONE" | "ACTIVE" | "AHEAD" | "GAP"}
 */
export function stepPosition(state, startedAt, endedAt) {
    if (state === "COMPLETE" || state === "SWEEP") return "DONE";
    if (state === "IN_PROGRESS" || state === "STALE") return "ACTIVE";
    if (state === "NO_DATA" && !startedAt && !endedAt) return "AHEAD";
    return "GAP";
}

/**
 * `meta.intervals` + one row -> the steps to render, plus the whole-order summary.
 *
 * Reads the server's interval list in the order the server gave it; this function sorts
 * nothing and invents nothing. An interval the row has no cell for still produces a step
 * (TatCell renders it as NO_DATA) rather than vanishing — a stage that is missing from
 * an order is a fact about that order, and silently dropping the row would hide it.
 *
 * @param {Array<{key:string,label:string,from?:string,to?:string,available?:boolean}>} intervals
 * @param {object} row - a TAT row: `intervals` (state+minutes per key) and `stages`.
 * @returns {{steps: Array<object>, summary: object|null, slowestKey: string|null}}
 */
export function buildStageJourney(intervals, row, unavailableStages = []) {
    const list = Array.isArray(intervals) ? intervals : [];
    const cells = row?.intervals || {};
    const stages = row?.stages || {};
    const actors = buildStageActors(row?.timeline);
    const unavailable = Array.isArray(unavailableStages) ? unavailableStages : [];

    const toStep = (interval) => {
        const cell = cells[interval.key] || null;
        const startedAt = stageTime(stages, interval.from);
        const endedAt = stageTime(stages, interval.to);
        const state = cell?.state ?? "NO_DATA";
        // The server explains WHY a stage is not instrumented; TatCell already surfaces
        // a cell's `note` on hover, so the explanation is threaded into the cell it
        // belongs to rather than given chrome of its own.
        const reason = unavailable.find((entry) => entry.key === interval.to)?.detail || null;
        return {
            ...interval,
            cell: cell && reason ? { ...cell, note: reason } : cell,
            minutes: cell?.minutes ?? null,
            state,
            startedAt,
            endedAt,
            position: stepPosition(state, startedAt, endedAt),
            // Attributed to the event that COMPLETED the stage — an interval's `to`
            // boundary is the one that closes it.
            attribution: stageAttribution(actors[interval.to]),
        };
    };

    const steps = list.filter((interval) => interval.key !== SUMMARY_INTERVAL_KEY).map(toStep);
    const summaryInterval = list.find((interval) => interval.key === SUMMARY_INTERVAL_KEY);
    const summary = summaryInterval ? toStep(summaryInterval) : null;

    // The slowest step is only meaningful against other measured steps — a single
    // measured stage is not "the slowest", it is the only one.
    const measured = steps.filter(
        (step) => typeof step.minutes === "number" && step.minutes >= SLOWEST_STAGE_FLOOR_MINUTES
    );
    const slowestKey =
        measured.length > 1
            ? measured.reduce((worst, step) => (step.minutes > worst.minutes ? step : worst)).key
            : null;

    // Scales the duration bars. Measured against the longest STEP, never against
    // end-to-end: every bar would otherwise be a sliver of the total and the chart
    // would say nothing.
    const longestMinutes = measured.reduce((max, step) => Math.max(max, step.minutes), 0);

    const current = steps.find((step) => step.position === "ACTIVE") || null;

    return { steps, summary, slowestKey, longestMinutes, current };
}

/**
 * Per-position treatment. One entry, read by the dot, the bar and the text together, so
 * a stage's position is legible three ways and never depends on hue alone.
 *
 * Every colour here already carries this exact meaning in TatCell — elm for a settled
 * measurement, blue for in-flight, orange for stuck. Nothing new is introduced, so
 * nothing new has to be learned.
 */
const POSITION_STYLES = {
    DONE: { dot: "h-[7px] w-[7px] bg-elm-500", bar: "bg-elm-400", label: "text-black" },
    ACTIVE: { dot: "h-[11px] w-[11px] bg-blue-600", bar: "bg-blue-500", label: "text-black" },
    // A real hole in data we do collect: the dot stays solid so the row does not read as
    // "nothing here yet", which is what AHEAD means.
    GAP: { dot: "h-[7px] w-[7px] bg-gray-400", bar: "bg-gray-300", label: "text-black" },
    // Road not yet travelled: hollow, and the whole row recedes.
    AHEAD: { dot: "h-[7px] w-[7px] bg-white-50 border border-gray-300", bar: "bg-gray-200", label: "text-gray-500" },
};

const STALLED_STYLE = { dot: "h-[11px] w-[11px] bg-orange-500", bar: "bg-orange-400", label: "text-black" };

/**
 * The order's whole journey as one object: a segment per stage, in order.
 *
 * This is the only element in the dialog allowed to be loud, and it earns it by being
 * the answer to the question the modal is opened to ask — how far along is this, and is
 * anything wrong. Seven segments read as a position at a glance where seven rows of
 * text read as a list that has to be worked through.
 */
function JourneyRail({ steps, slowestKey }) {
    const done = steps.filter((step) => step.position === "DONE").length;

    return (
        <div
            role="img"
            aria-label={`${done} of ${steps.length} stages complete`}
            className="flex w-full gap-1"
        >
            {steps.map((step) => {
                const stalled = step.state === "STALE" || step.key === slowestKey;
                const style = stalled ? STALLED_STYLE : POSITION_STYLES[step.position];
                return (
                    <span
                        key={step.key}
                        data-tat-rail={stalled ? "STALE" : step.position}
                        title={step.label}
                        className={`h-1.5 flex-1 rounded-full ${style.bar}`}
                    />
                );
            })}
        </div>
    );
}

JourneyRail.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.object).isRequired,
    slowestKey: PropTypes.string,
};

JourneyRail.defaultProps = { slowestKey: null };

/**
 * Duration as length, not only as a number.
 *
 * Without this a 28-minute stage and a 3-minute stage are the same size on screen and
 * the reader has to compare seven figures by eye to find where the time went — which is
 * the one thing this dialog exists to make obvious. Scaled against the longest step, so
 * the worst stage is always a full track and the rest are read against it.
 */
function DurationBar({ minutes, longestMinutes, className }) {
    if (typeof minutes !== "number" || minutes <= 0 || !longestMinutes) return null;
    // Floored at 4% so a genuinely tiny stage is still a visible mark rather than
    // disappearing into the track, which would read as "no measurement".
    const share = Math.max(4, Math.round((minutes / longestMinutes) * 100));

    return (
        <span aria-hidden="true" className="hidden h-1 w-14 overflow-hidden rounded-full bg-gray-100 sm:block">
            <span className={`block h-full rounded-full ${className}`} style={{ width: `${share}%` }} />
        </span>
    );
}

DurationBar.propTypes = {
    minutes: PropTypes.number,
    longestMinutes: PropTypes.number,
    className: PropTypes.string,
};

DurationBar.defaultProps = { minutes: null, longestMinutes: 0, className: "bg-gray-300" };

/** One measured span: what it is, when it ran, how long it took. */
function StageStep({ step, isSlowest, longestMinutes, showDate }) {
    const stalled = step.state === "STALE" || isSlowest;
    const style = stalled ? STALLED_STYLE : POSITION_STYLES[step.position];
    const span = formatSpan(step.startedAt, step.endedAt, showDate);
    const ahead = step.position === "AHEAD";

    return (
        <li className={`flex items-start gap-3 py-2.5 ${ahead ? "opacity-60" : ""}`}>
            <span className="flex h-[20px] w-[11px] shrink-0 items-center justify-center">
                <span
                    aria-hidden="true"
                    data-tat-step={stalled ? "slowest" : step.position}
                    className={`block rounded-full ring-2 ring-white-50 ${style.dot}`}
                />
            </span>

            <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <div className="min-w-0">
                    <div className={`font-sans text-[13px] font-semibold leading-snug ${style.label}`}>
                        {step.label}
                    </div>
                    {(span || step.attribution) && (
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 font-sans text-[11px] text-gray-500">
                            {span && <span className="tabular-nums">{span}</span>}
                            {step.attribution && (
                                <span data-tat-attribution="true">{step.attribution}</span>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 items-center gap-2.5">
                    {isSlowest && (
                        <span
                            data-tat-slowest-stage="true"
                            className="font-sans text-[10px] font-semibold uppercase text-orange-700"
                        >
                            Slowest
                        </span>
                    )}
                    <DurationBar
                        minutes={step.minutes}
                        longestMinutes={longestMinutes}
                        className={style.bar}
                    />
                    {ahead ? (
                        // Deliberately NOT TatCell. The server's NO_DATA means "we measure
                        // this and this order lacks it", which is the wrong sentence for a
                        // stage the order has not reached — see stepPosition's comment. The
                        // state itself is untouched; only the wording of its absence changes.
                        <span
                            data-tat-ahead="true"
                            title="This order has not reached this stage yet."
                            className="font-sans text-[11px] italic text-gray-400"
                        >
                            Not reached
                        </span>
                    ) : (
                        <TatCell value={step.cell} />
                    )}
                </div>
            </div>
        </li>
    );
}

StageStep.propTypes = {
    step: PropTypes.object.isRequired,
    isSlowest: PropTypes.bool,
    longestMinutes: PropTypes.number,
    showDate: PropTypes.bool,
};

StageStep.defaultProps = { isSlowest: false, longestMinutes: 0, showDate: true };

/** One headline fact about the whole order. */
function SummaryStat({ label, value, hint, tone }) {
    return (
        <div className="flex flex-col gap-0.5">
            {/* Sentence case, not the tracked-out caps this sat in before: a row of
                shouting labels over three small figures made the labels the loudest
                thing in a panel whose whole job is the figures. */}
            <span className="font-sans text-[11px] text-gray-500">{label}</span>
            <span className={`font-sans text-[17px] font-bold leading-tight tabular-nums ${tone}`}>
                {value}
            </span>
            {hint && <span className="font-sans text-[10px] text-gray-500">{hint}</span>}
        </div>
    );
}

SummaryStat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    hint: PropTypes.node,
    tone: PropTypes.string,
};

SummaryStat.defaultProps = { hint: null, tone: "text-black" };

function TatTimelineModal({ row, intervals, unavailableStages, onClose }) {
    // The legacy Modal closes on backdrop click but has no Escape handler. Added here
    // rather than in the shared legacy component so this change cannot alter the
    // behaviour of the other screens already mounting it.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    const journey = useMemo(
        () => buildStageJourney(intervals, row, unavailableStages),
        [intervals, row, unavailableStages]
    );

    if (!row) return null;

    const { steps, summary, slowestKey, longestMinutes, current } = journey;
    const slowest = steps.find((step) => step.key === slowestKey) || null;
    const doneCount = steps.filter((step) => step.position === "DONE").length;
    const journeyDate = singleDateFor(steps);

    return (
        <Modal title={`Order ${row.id} stage timeline`} onClose={onClose}>
            <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <span className="font-sans text-[15px] font-bold text-black">{row.patientName}</span>
                        {journeyDate && (
                            <span className="font-sans text-[11px] text-gray-500">{journeyDate}</span>
                        )}
                    </div>

                    {steps.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <JourneyRail steps={steps} slowestKey={slowestKey} />
                            {/* The live sentence. For an order still moving this is the
                                single most useful fact in the dialog and it was previously
                                buried in whichever row happened to be in flight. */}
                            <span className="font-sans text-[11px] text-gray-500">
                                {current ? (
                                    <>
                                        Now in{" "}
                                        <span className="font-semibold text-black">{current.label}</span>
                                        {typeof current.minutes === "number"
                                            ? ` · ${formatDuration(current.minutes)} so far`
                                            : ""}
                                    </>
                                ) : (
                                    `${doneCount} of ${steps.length} stages measured`
                                )}
                            </span>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-x-10 gap-y-3 rounded-xl bg-gray-50 px-4 py-3">
                        <SummaryStat
                            label="End to end"
                            // The server measures this span itself rather than it being the
                            // sum of the steps — the steps branch and overlap, so adding
                            // them up would double-count.
                            value={
                                summary && typeof summary.minutes === "number"
                                    ? formatDuration(summary.minutes)
                                    : "—"
                            }
                            hint={summary && summary.state !== "COMPLETE" ? "still open" : null}
                        />
                        <SummaryStat label="Stages measured" value={`${doneCount} of ${steps.length}`} />
                        {slowest && (
                            <SummaryStat
                                label="Slowest stage"
                                value={formatDuration(slowest.minutes)}
                                hint={slowest.label}
                                tone="text-orange-700"
                            />
                        )}
                    </div>
                </div>

                {steps.length === 0 ? (
                    <EmptyState
                        title="No stages to show"
                        hint="The TAT service returned no interval definitions, so there is nothing to lay out for this order."
                    />
                ) : (
                    // One spine behind the whole list; each dot punches through it with a
                    // white ring rather than interrupting it.
                    <ol className="relative flex flex-col border-t border-gray-100 pt-1">
                        <span
                            aria-hidden="true"
                            className="absolute left-[5px] top-5 bottom-5 w-px bg-gray-200"
                        />
                        {steps.map((step) => (
                            <StageStep
                                key={step.key}
                                step={step}
                                isSlowest={step.key === slowestKey}
                                longestMinutes={longestMinutes}
                                showDate={!journeyDate}
                            />
                        ))}
                    </ol>
                )}
            </div>
        </Modal>
    );
}

TatTimelineModal.propTypes = {
    /** A TAT row: `intervals` (state+minutes per key) and `stages` (raw timestamps). */
    row: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        patientName: PropTypes.string,
        intervals: PropTypes.object,
        stages: PropTypes.object,
    }),
    /** The API's `meta.intervals` — the same list the table's columns are built from. */
    intervals: PropTypes.arrayOf(PropTypes.object),
    /** The API's `meta.unavailableStages` — why a stage carries no measurement here. */
    unavailableStages: PropTypes.arrayOf(PropTypes.object),
    onClose: PropTypes.func.isRequired,
};

TatTimelineModal.defaultProps = { row: null, intervals: [], unavailableStages: [] };

export default TatTimelineModal;
